import { create } from 'zustand';
import type { Note, NoteCategory } from '@/core/models/note';

interface NoteState {
  byId: Record<string, Note>;
  ids: string[];
  total: number;
  page: number;
  pageSize: number;
  category: NoteCategory | 'all';
  loading: boolean;
  loadingMore: boolean;
  refreshing: boolean;
  hasMore: boolean;
  /** Monotonic token for in-flight `list` requests; older results are dropped. */
  fetchToken: number;

  setCategory: (c: NoteState['category']) => void;
  setList: (items: Note[], total: number, page: number, pageSize: number) => void;
  appendList: (items: Note[], page: number) => void;
  upsert: (note: Note) => void;
  remove: (id: string) => void;
  setLoading: (v: boolean) => void;
  setLoadingMore: (v: boolean) => void;
  setRefreshing: (v: boolean) => void;
  nextFetchToken: () => number;
  isCurrentToken: (token: number) => boolean;
}

export const useNoteStore = create<NoteState>((set, get) => ({
  byId: {},
  ids: [],
  total: 0,
  page: 1,
  pageSize: 20,
  category: 'all',
  loading: false,
  loadingMore: false,
  refreshing: false,
  hasMore: false,
  fetchToken: 0,

  // category switch keeps the existing list visible while the new fetch
  // resolves — no `ids: []` flash. setList replaces atomically when results
  // arrive.
  setCategory: (category) => set({ category }),

  setList: (items, total, page, pageSize) =>
    set((prev) => {
      const byId = { ...prev.byId };
      for (const n of items) byId[n.id] = n;
      return {
        byId,
        ids: items.map((n) => n.id),
        total,
        page,
        pageSize,
        hasMore: items.length === pageSize && page * pageSize < total,
      };
    }),

  appendList: (items, page) =>
    set((prev) => {
      const byId = { ...prev.byId };
      const seen = new Set(prev.ids);
      const newIds: string[] = [];
      for (const n of items) {
        byId[n.id] = n;
        if (!seen.has(n.id)) {
          seen.add(n.id);
          newIds.push(n.id);
        }
      }
      return {
        byId,
        ids: [...prev.ids, ...newIds],
        page,
        hasMore: items.length === prev.pageSize && page * prev.pageSize < prev.total,
      };
    }),

  upsert: (note) =>
    set((prev) => {
      const byId = { ...prev.byId, [note.id]: note };
      const ids = prev.ids.includes(note.id) ? prev.ids : [note.id, ...prev.ids];
      return { byId, ids };
    }),

  remove: (id) =>
    set((prev) => {
      const byId = { ...prev.byId };
      delete byId[id];
      return { byId, ids: prev.ids.filter((x) => x !== id) };
    }),

  setLoading: (loading) => set({ loading }),
  setLoadingMore: (loadingMore) => set({ loadingMore }),
  setRefreshing: (refreshing) => set({ refreshing }),

  nextFetchToken: () => {
    const next = get().fetchToken + 1;
    set({ fetchToken: next });
    return next;
  },
  isCurrentToken: (token) => get().fetchToken === token,
}));
